// Visor de EJEMPLOS de un modo — lo abre el botón "Ver ejemplos" de la
// pantalla Modos. Desktop: popup centrado de dos columnas (izquierda: título,
// descripción y CTA; derecha: el carrusel de ejemplos). Mobile: pantalla
// completa, con entrada/salida animada sutil (fade + deslizamiento).
//
// Portaleado a document.body: igual que FeatureLockedModal — el panel de
// contenido desktop es position:fixed y un overlay inline quedaría atrapado
// bajo el sidebar (ver memoria del proyecto sobre stacking).
//
// Animación de cierre: el componente queda montado ~200ms después de pedir el
// cierre (requestClose baja `shown` y recién después dispara onClose), así la
// transición de salida se ve. Todos los caminos de cierre (X, backdrop,
// Escape, CTA) pasan por requestClose.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight, Sparkles, Lock } from "lucide-react";
import type { ModeChatMessage, ModeExample } from "../modeExamples";

const CLOSE_ANIM_MS = 200;
// Cadencia con la que aparecen las burbujas del ejemplo tipo chat (un
// mensaje puede pedir una pausa mayor vía `delayAfterMs`, ej. para dejar
// que su video se reproduzca antes de seguir).
const CHAT_MSG_INTERVAL_MS = 2200;

/** Conversación simulada del ejemplo tipo 'chat' (modo Asistente): las
 *  burbujas aparecen una por una y el scroll acompaña, como un chat real. */
const ChatExampleView: React.FC<{ messages: ModeChatMessage[] }> = ({
  messages,
}) => {
  const [visibleCount, setVisibleCount] = useState(1);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setVisibleCount(1);
    let timer = 0;
    const scheduleNext = (count: number) => {
      if (count >= messages.length) return;
      // La pausa la define el mensaje que ACABA de aparecer (delayAfterMs),
      // o la cadencia estándar.
      const delay =
        messages[count - 1]?.delayAfterMs ?? CHAT_MSG_INTERVAL_MS;
      timer = window.setTimeout(() => {
        setVisibleCount(count + 1);
        scheduleNext(count + 1);
      }, delay);
    };
    scheduleNext(1);
    return () => window.clearTimeout(timer);
  }, [messages]);

  const scrollToEnd = useCallback(() => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, []);

  // Acompañar cada burbuja nueva (la media re-scrollea en su onLoad, porque
  // recién ahí se conoce su alto).
  useEffect(() => {
    scrollToEnd();
  }, [visibleCount, scrollToEnd]);

  return (
    <div
      ref={scrollRef}
      className="h-full w-full overflow-y-auto p-1 sm:max-w-[400px]"
    >
      {/* Los mensajes fluyen desde ARRIBA (sin justify-end): el primero
          queda arriba y el scroll acompaña hacia abajo. */}
      <div className="flex flex-col gap-2.5">
        {messages.slice(0, visibleCount).map((m, i) => {
          const isUser = m.role === "user";
          return (
            <div
              key={i}
              className={`animate-chat-msg-in flex ${
                isUser ? "justify-end" : "justify-start"
              }`}
            >
              <div className={`flex max-w-[80%] flex-col gap-1.5 ${isUser ? "items-end" : "items-start"}`}>
                {m.text && (
                  <div
                    className={`rounded-2xl px-3.5 py-2 text-[13.5px] leading-snug ${
                      isUser
                        ? "rounded-br-md bg-brand-primary text-white"
                        : "rounded-bl-md bg-neutral-100 text-brand-black"
                    }`}
                  >
                    {m.text}
                  </div>
                )}
                {m.imageUrl && (
                  <img
                    src={m.imageUrl}
                    alt=""
                    onLoad={scrollToEnd}
                    className="w-[210px] rounded-xl bg-neutral-100"
                  />
                )}
                {/* Autoplay muteado (el autoplay con sonido lo bloquea el
                    browser): arranca solo al aparecer la burbuja; con los
                    controls se puede des-mutear. */}
                {m.videoUrl && (
                  <video
                    src={m.videoUrl}
                    poster={m.videoPoster}
                    autoPlay
                    muted
                    loop
                    controls
                    playsInline
                    preload="metadata"
                    onLoadedMetadata={scrollToEnd}
                    className="w-[210px] rounded-xl bg-neutral-100"
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export interface ModeExamplesModalProps {
  open: boolean;
  onClose: () => void;
  /** Título del modo (columna izquierda / header mobile). */
  title: string;
  /** Descripción del modo — debajo del título. */
  description?: string;
  examples: ModeExample[];
  /** CTA del visor ("Generar", o "Ver planes" si el modo está bloqueado).
   *  Desktop: columna izquierda. Mobile: abajo del todo, full-width.
   *  Cierra el visor y ejecuta la acción. */
  actionLabel?: string;
  onAction?: () => void;
  /** true si el modo está bloqueado por plan → el CTA es "Mejorar plan"
   *  (sin ícono de generar). */
  locked?: boolean;
  /** Nota del plan requerido (ej. "Función Pro — disponible para los planes
   *  Pro y Ultra."). Se muestra con candado debajo de la descripción. */
  planNote?: string;
}

export const ModeExamplesModal: React.FC<ModeExamplesModalProps> = ({
  open,
  onClose,
  title,
  description,
  examples,
  actionLabel,
  onAction,
  locked,
  planNote,
}) => {
  const [idx, setIdx] = useState(0);
  // Drive de las transiciones CSS: monta oculto y pasa a visible al frame
  // siguiente (entrada); vuelve a oculto antes de desmontar (salida).
  const [shown, setShown] = useState(false);

  // Arrancar siempre desde el primer ejemplo al abrir.
  useEffect(() => {
    if (open) setIdx(0);
  }, [open]);

  useEffect(() => {
    if (!open) {
      setShown(false);
      return;
    }
    const id = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(id);
  }, [open]);

  const requestClose = useCallback(() => {
    setShown(false);
    window.setTimeout(onClose, CLOSE_ANIM_MS);
  }, [onClose]);

  // Escape cierra; ← → navegan. Bloqueo del scroll del body mientras está
  // abierto (en mobile el body es el scroller de la página).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") requestClose();
      if (e.key === "ArrowLeft") setIdx((i) => Math.max(0, i - 1));
      if (e.key === "ArrowRight")
        setIdx((i) => Math.min(examples.length - 1, i + 1));
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, requestClose, examples.length]);

  if (!open || examples.length === 0) return null;

  const current = examples[Math.min(idx, examples.length - 1)];
  const hasPrev = idx > 0;
  const hasNext = idx < examples.length - 1;

  return createPortal(
    <div
      className={`fixed inset-0 z-[9999] sm:flex sm:items-center sm:justify-center sm:p-6 sm:bg-black/60 sm:backdrop-blur-sm transition-opacity duration-200 ${
        shown ? "opacity-100" : "opacity-0"
      }`}
      onClick={requestClose}
    >
      <div
        className={`relative flex h-full w-full flex-col overflow-hidden bg-white sm:h-auto sm:w-[780px] sm:max-w-[94vw] sm:flex-row sm:rounded-2xl sm:shadow-2xl transition-transform duration-200 ease-out ${
          shown
            ? "translate-y-0 sm:scale-100"
            : "translate-y-4 sm:translate-y-0 sm:scale-[0.98]"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Cerrar — arriba a la derecha del popup/pantalla. */}
        <button
          type="button"
          onClick={requestClose}
          aria-label="Cerrar"
          className="absolute right-4 top-[calc(env(safe-area-inset-top,0px)+0.875rem)] z-10 flex h-9 w-9 items-center justify-center rounded-full bg-neutral-100 hover:bg-neutral-200 text-brand-black transition-colors sm:top-4"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Columna izquierda (desktop) / encabezado (mobile): título,
            descripción y CTA (el CTA solo en desktop). */}
        <div className="px-4 pr-14 pt-[calc(env(safe-area-inset-top,0px)+0.875rem)] sm:flex sm:w-[290px] sm:shrink-0 sm:flex-col sm:justify-center sm:gap-4 sm:p-7 sm:pr-3">
          <div>
            <p className="text-[12px] font-medium uppercase tracking-wide text-[#3C3C43]/50">
              Ejemplos
            </p>
            <h3 className="text-[16px] font-semibold text-brand-black sm:text-[20px] sm:leading-tight">
              {title}
            </h3>
          </div>
          {description && (
            <p className="pt-1 text-[13.5px] leading-snug text-[#3C3C43]/70 sm:pt-0 sm:text-[14px]">
              {description}
            </p>
          )}
          {/* Modo bloqueado: aclarar qué plan requiere. En mobile esta nota
              va abajo, pegada al CTA "Mejorar plan" (ver columna derecha). */}
          {planNote && (
            <p className="hidden sm:flex items-start gap-1.5 text-[13px] font-medium leading-snug text-brand-primary">
              <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              {planNote}
            </p>
          )}
          {onAction && actionLabel && (
            <button
              type="button"
              onClick={() => {
                requestClose();
                onAction();
              }}
              className="hidden sm:inline-flex items-center gap-1.5 self-start rounded-full bg-brand-primary px-5 py-2.5 text-[14px] font-semibold text-white shadow-lg transition-transform hover:bg-brand-primary-hover active:scale-[0.97]"
            >
              {!locked && <Sparkles className="h-4 w-4" />}
              {actionLabel}
            </button>
          )}
        </div>

        {/* Columna derecha: media + controles + caption. El propio
            <img>/<video> es el container redondeado — con max-w/max-h + auto
            conserva su aspect ratio y el recuadro abraza al ejemplo. */}
        <div className="flex min-h-0 flex-1 flex-col gap-3 px-4 pb-[calc(env(safe-area-inset-bottom,0px)+1rem)] pt-3 sm:p-6 sm:pl-2">
          <div className="flex min-h-0 flex-1 items-center justify-center sm:h-[min(56vh,520px)] sm:flex-none">
            {/* Sin `muted`: el ejemplo se escucha con su voz. El autoplay con
                sonido puede ser bloqueado por el browser — en ese caso queda
                el poster con los controls y el user toca play. */}
            {current.type === "chat" ? (
              <ChatExampleView key={idx} messages={current.messages} />
            ) : current.type === "video" ? (
              <video
                key={`${idx}-${current.url}`}
                src={current.url}
                poster={current.poster}
                autoPlay
                loop
                playsInline
                controls
                className="h-auto max-h-full w-auto max-w-full rounded-xl bg-neutral-100"
              />
            ) : (
              <img
                key={`${idx}-${current.url}`}
                src={current.url}
                alt={current.caption ?? title}
                className="h-auto max-h-full w-auto max-w-full rounded-xl bg-neutral-100"
              />
            )}
          </div>

          {/* Controles del carrusel debajo de la media: ← 1/3 → (solo con
              más de un ejemplo). Los extremos se deshabilitan, no se ocultan,
              para que la fila no salte de layout. */}
          {examples.length > 1 && (
            <div className="flex items-center justify-center gap-4">
              <button
                type="button"
                disabled={!hasPrev}
                onClick={() => setIdx((i) => Math.max(0, i - 1))}
                aria-label="Ejemplo anterior"
                className="flex h-10 w-10 items-center justify-center rounded-full bg-neutral-100 hover:bg-neutral-200 text-brand-black transition-colors disabled:opacity-40 disabled:pointer-events-none"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <span className="min-w-[44px] text-center text-[13px] font-medium text-[#3C3C43]/70">
                {idx + 1} / {examples.length}
              </span>
              <button
                type="button"
                disabled={!hasNext}
                onClick={() =>
                  setIdx((i) => Math.min(examples.length - 1, i + 1))
                }
                aria-label="Ejemplo siguiente"
                className="flex h-10 w-10 items-center justify-center rounded-full bg-neutral-100 hover:bg-neutral-200 text-brand-black transition-colors disabled:opacity-40 disabled:pointer-events-none"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          )}

          {/* Caption opcional del ejemplo, debajo de los controles. */}
          {current.caption && (
            <p className="text-[13.5px] leading-snug text-[#3C3C43]/70">
              {current.caption}
            </p>
          )}

          {/* Nota del plan requerido en mobile: justo arriba del CTA. */}
          {planNote && (
            <p className="sm:hidden flex items-start justify-center gap-1.5 text-[13px] font-medium leading-snug text-brand-primary">
              <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              {planNote}
            </p>
          )}
          {/* CTA en mobile: abajo del todo, full-width (en desktop vive en
              la columna izquierda). */}
          {onAction && actionLabel && (
            <button
              type="button"
              onClick={() => {
                requestClose();
                onAction();
              }}
              className="sm:hidden mt-1 inline-flex w-full items-center justify-center gap-1.5 rounded-full bg-brand-primary px-6 py-3 text-[15px] font-semibold text-white shadow-lg transition-transform hover:bg-brand-primary-hover active:scale-[0.97]"
            >
              {!locked && <Sparkles className="h-4 w-4" />}
              {actionLabel}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ModeExamplesModal;
